import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  usePopper,
  FilterButton,
  SearchableTextField,
  Button,
  PlusIcon,
  useTranslations,
  Tooltip,
  ActionButton,
  Divider,
  ShareIcon,
  DeleteIcon,
  encryptSymmetric,
  useHeroSnackbar,
  ErrorIcon,
  useMapKeyValueExtractor,
  PromptModal,
  PanelContent,
  ISearchableTextFieldProps,
  IFilterButtonProps,
  MoveIcon,
  Typography,
  MoveToEmployeeIcon,
  MoveToGroupIcon,
  SeverityEnum,
} from '@uniqkey-frontend/shared-app';
import { Ownership } from '@uniqkey-backend-organization-web/api-client';
import OrganizationLoginsTable, {
  IOrganizationLoginsTableRow,
} from '../../../../components/tables/OrganizationLoginsTable';
import useOrganizationLoginsTable from '../../../../hooks/tables/useOrganizationLoginsTable';
import OrganizationLoginsTabFilter, {
  IOrganizationLoginsTabFilterSubmitResult,
} from './components/OrganizationLoginsTabFilter';
import CreateLoginModal, { ICreateLoginReturnValue } from '../../../../components/CreateLoginModal';
import useVaultsAPI from '../../../../hooks/useVaultsAPI';
import { useCompanionApplicationContext } from '../../../../contexts/CompanionApplicationContext';
import ShareVaultsToGroupModal from '../../../../components/ShareVaultsToGroupModal';
import { useDetachVaults, useDeleteVaults } from '../../../../hooks/reactQuery';
import VaultTypeEnum from '../../../../enums/VaultTypeEnum';
import { logException } from '../../../../services/sentryService';
import HostTypeEnum from '../../../../enums/HostTypeEnum';
import { getActiveOrganizationId } from '../../../../services/organizationService';
import { useTrustedPortalStore } from '../../../../modules/TrustedPortalModule/store';
import MoveVaultsToEmployeeModal from '../../../../components/MoveVaultsToEmployeeModal';
import MoveVaultsToGroupModal from '../../../../components/MoveVaultsToGroupModal';
import { getTranslationKeyByError } from '../../../../helpers/errorService';

const OrganizationLoginsTab = () => {
  const { t } = useTranslations();
  const [isCreateLoginModalOpen, setIsCreateLoginModalOpen] = useState(false);
  const [isShareLoginsModalOpen, setIsShareLoginsModalOpen] = useState(false);
  const [isCreateLoginLoading, setIsCreateLoginLoading] = useState(false);
  const [isDetachLoginModalOpen, setIsDetachLoginModalOpen] = useState(false);
  const [isDetachLoginLoading, setIsDetachLoginLoading] = useState(false);
  const [isDeleteLoginModalOpen, setIsDeleteLoginModalOpen] = useState(false);
  const [isDeleteLoginLoading, setIsDeleteLoginLoading] = useState(false);
  const [isMoveToPrivateLoginsModalOpen, setIsMoveToPrivateLoginsModalOpen] = useState(false);
  const [isMoveToPrivateLoginsLoading, setIsMoveToPrivateLoginsLoading] = useState(false);
  const [isMoveToEmployeeLoginsModalOpen, setIsMoveToEmployeeLoginsModalOpen] = useState(
    false,
  );
  const [isMoveToGroupLoginsModalOpen, setIsMoveToGroupLoginsModalOpen] = useState(
    false,
  );

  const {
    isOpen: isFilterOpen,
    anchorEl: filterAnchorEl,
    setPopperIsOpen: setIsFilterOpen,
  } = usePopper();
  const toggleIsFilterOpen = useCallback<NonNullable<IFilterButtonProps['onChange']>>(
    (event) => setIsFilterOpen(!isFilterOpen, event),
    [setIsFilterOpen, isFilterOpen],
  );
  const {
    selectedLogins,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    resetActivePage,
    resetSelectedRows,
    ...restTableProps
  } = useOrganizationLoginsTable({
    noDataMessageKey: 'organizationLoginsPage.noData',
  });
  const {
    values: selectedLoginsAsObjects, keys: selectedLoginsIds,
  } = useMapKeyValueExtractor<IOrganizationLoginsTableRow>(selectedLogins);

  const handleFilterClose = useCallback(
    () => setIsFilterOpen(false),
    [setIsFilterOpen],
  );
  const handleCreateLoginModalOpen = useCallback(() => setIsCreateLoginModalOpen(true), []);
  const handleCreateLoginModalClose = useCallback(() => setIsCreateLoginModalOpen(false), []);
  const handleDetachLoginModalOpen = useCallback(() => setIsDetachLoginModalOpen(true), []);
  const handleDetachLoginModalClose = useCallback(() => setIsDetachLoginModalOpen(false), []);
  const handleDeleteLoginModalOpen = useCallback(() => setIsDeleteLoginModalOpen(true), []);
  const handleDeleteLoginModalClose = useCallback(() => setIsDeleteLoginModalOpen(false), []);
  const handleShareLoginsModalOpen = useCallback(() => setIsShareLoginsModalOpen(true), []);
  const handleShareLoginsModalClose = useCallback(() => setIsShareLoginsModalOpen(false), []);
  const handleMoveToPrivateLoginsModalOpen = useCallback(
    () => setIsMoveToPrivateLoginsModalOpen(true),
    [],
  );
  const handleMoveToPrivateLoginsModalClose = useCallback(
    () => setIsMoveToPrivateLoginsModalOpen(false),
    [],
  );
  const handleMoveToEmployeeLoginsModalOpen = useCallback(
    () => setIsMoveToEmployeeLoginsModalOpen(true),
    [],
  );
  const handleMoveToEmployeeLoginsModalClose = useCallback(
    () => setIsMoveToEmployeeLoginsModalOpen(false),
    [],
  );
  const handleMoveToGroupLoginsModalOpen = useCallback(
    () => setIsMoveToGroupLoginsModalOpen(true),
    [],
  );
  const handleMoveToGroupLoginsModalClose = useCallback(
    () => setIsMoveToGroupLoginsModalOpen(false),
    [],
  );

  const { createVaultPasswords, moveVaultsToPrivateKeychain } = useVaultsAPI();
  const { showError, showWarning, showSuccess } = useHeroSnackbar();
  const { symmetricKey } = useCompanionApplicationContext();
  const { mutate: mutateDetachVault } = useDetachVaults();
  const { mutate: mutateDeleteVault } = useDeleteVaults();

  const activeOrganizationId = getActiveOrganizationId();
  const isTrustedPortalEnabled = useTrustedPortalStore.useIsEnabledByOrganizationId()[
    activeOrganizationId!
  ] ?? false;

  const handleSearchChange = useCallback<ISearchableTextFieldProps['onChange']>(
    (debouncedValue) => {
      setSearchQuery(debouncedValue);
      resetActivePage();
    },
    [setSearchQuery, resetActivePage],
  );

  const handleFilterSubmit = useCallback((
    updatedValues: IOrganizationLoginsTabFilterSubmitResult,
  ) => {
    setFilterValues(updatedValues);
    resetActivePage();
  }, [setFilterValues, resetActivePage]);

  const {
    isMoveToPrivateDisabled,
    isMoveToEmployeeDisabled,
    isMoveToGroupDisabled,
    isShareDisabled,
    isRemoveDisabled,
    isDeleteDisabled,
    moveToPrivateTooltipTitle,
    moveToEmployeeTooltipTitle,
    moveToGroupTooltipTitle,
    shareTooltipTitle,
    removeTooltipTitle,
    deleteTooltipTitle,
  } = useMemo(() => {
    if (!selectedLoginsAsObjects.length) {
      return {
        isMoveToPrivateDisabled: true,
        isMoveToEmployeeDisabled: true,
        isMoveToGroupDisabled: true,
        isShareDisabled: true,
        isRemoveDisabled: true,
        isDeleteDisabled: true,
        moveToPrivateTooltipTitle: t('common.tickRow'),
        moveToEmployeeTooltipTitle: t('common.tickRow'),
        moveToGroupTooltipTitle: t('common.tickRow'),
        shareTooltipTitle: t('common.tickRow'),
        removeTooltipTitle: t('common.tickRow'),
        deleteTooltipTitle: t('common.tickRow'),
      };
    }
    let disableShare = false;
    let disableRemove = false;
    let disableDelete = false;
    let disableMoveToPrivate = false;
    let moveToPrivateTitle = t('organizationLoginsPage.moveToPrivate.active');
    let shareTitle = t('organizationLoginsPage.shareToGroup.active');
    let removeTitle = t('organizationLoginsPage.remove.active');
    let deleteTitle = t('organizationLoginsPage.delete.active');
    selectedLoginsAsObjects.forEach(({ ownership }) => {
      if (ownership === Ownership.Unmanaged) {
        disableRemove = true;
        removeTitle = t('organizationLoginsPage.remove.disabled');
        disableShare = true;
        shareTitle = t('organizationLoginsPage.shareToGroup.disabled');
        disableMoveToPrivate = true;
        moveToPrivateTitle = t('organizationLoginsPage.moveToPrivate.disabled');
      }
      if (ownership === Ownership.Employees) {
        disableShare = true;
        shareTitle = t('organizationLoginsPage.shareToGroup.disabled');
        disableDelete = true;
        deleteTitle = t('organizationLoginsPage.delete.disabled');
      }
      if (ownership === Ownership.Groups) {
        disableDelete = true;
        deleteTitle = t('organizationLoginsPage.delete.disabled');
        disableMoveToPrivate = true;
        moveToPrivateTitle = t('organizationLoginsPage.moveToPrivate.disabled');
      }
    });
    const uniqueOwnershipCount = new Set(selectedLoginsAsObjects.map(({ ownership }) => ownership))
      .size;
    if (uniqueOwnershipCount > 1) {
      disableShare = true;
      disableRemove = true;
      disableDelete = true;
      disableMoveToPrivate = true;
      shareTitle = t('common.valuesInManagedByShouldBeIdentical');
      removeTitle = t('common.valuesInManagedByShouldBeIdentical');
      deleteTitle = t('common.valuesInManagedByShouldBeIdentical');
      moveToPrivateTitle = t('common.valuesInManagedByShouldBeIdentical');
    }
    return {
      isMoveToPrivateDisabled: disableMoveToPrivate,
      isMoveToEmployeeDisabled: false,
      isMoveToGroupDisabled: false,
      isShareDisabled: disableShare,
      isRemoveDisabled: disableRemove,
      isDeleteDisabled: disableDelete,
      moveToPrivateTooltipTitle: moveToPrivateTitle,
      moveToEmployeeTooltipTitle: t('organizationLoginsPage.moveToEmployee.active'),
      moveToGroupTooltipTitle: t('organizationLoginsPage.moveToGroup.active'),
      shareTooltipTitle: shareTitle,
      removeTooltipTitle: removeTitle,
      deleteTooltipTitle: deleteTitle,
    };
  }, [selectedLoginsAsObjects, t]);

  const ownership = useMemo(() => {
    if (selectedLoginsAsObjects.length) {
      return selectedLoginsAsObjects[0].ownership;
    }
    return null;
  }, [selectedLoginsAsObjects]);

  const handleCreateLogin = useCallback(async (loginParams: ICreateLoginReturnValue) => {
    const {
      name, additionalInfo, username, password, hostType, website, desktopApp,
    } = loginParams;
    setIsCreateLoginLoading(true);
    try {
      let payload = {
        name,
        username,
        password: encryptSymmetric({
          key: symmetricKey,
          string: password,
          fallbackValue: null,
        }),
        applicationNames: ['todo-remove-me-later.com'], // TODO: remove this
        additionalInfo,
      };
      if (hostType === HostTypeEnum.Website) {
        payload = { ...payload, ...(website && { webAddress: website }) };
      }
      if (hostType === HostTypeEnum.DesktopApp) {
        payload = {
          ...payload,
          ...(desktopApp?.value && {
            desktopApplicationName: desktopApp.value,
            applicationId: desktopApp.id,
          }),
        };
      }
      await createVaultPasswords(payload);
      if (isTrustedPortalEnabled) {
        showSuccess({
          text: t('trustedPortalSuccessNotifications.login.created'),
        });
      } else {
        showWarning({
          text: t('createLoginModal.loginWillBeShown'),
        });
      }
      handleCreateLoginModalClose();
    } catch (e) {
      showError({
        text: t('common.somethingWentWrong'),
      });
      logException(e, {
        message: 'OrganizationLoginsPage/handleCreateLogin exception',
      });
    } finally {
      setIsCreateLoginLoading(false);
    }
  }, [
    symmetricKey,
    handleCreateLoginModalClose,
    createVaultPasswords,
    isTrustedPortalEnabled,
    showSuccess,
    showWarning,
    showError,
    t,
  ]);

  const handleDetachLogin = useCallback(async () => {
    setIsDetachLoginLoading(true);
    mutateDetachVault({
      vaultIds: selectedLoginsIds,
    }, {
      onSuccess: ({ failCount, successCount }) => {
        if (successCount) {
          showSuccess({
            text: t(
              'organizationLoginsPage.detachLoginModal.successMessage',
              { count: successCount },
            ),
          });
        }
        if (failCount) {
          showError({
            text: t('organizationLoginsPage.detachLoginModal.errorMessage', { count: failCount }),
          });
        }
        handleDetachLoginModalClose();
        resetSelectedRows();
        resetActivePage();
      },
      onError: (e) => {
        showError({ text: t(getTranslationKeyByError(e)) });
        logException(e, {
          message: 'OrganizationLoginsTab/handleDetachLogin exception',
        });
      },
      onSettled: () => setIsDetachLoginLoading(false),
    });
  }, [
    selectedLoginsIds,
    mutateDetachVault,
    handleDetachLoginModalClose,
    resetSelectedRows,
    resetActivePage,
    showSuccess,
    showError,
    t,
  ]);

  const handleDeleteLogin = useCallback(async () => {
    setIsDeleteLoginLoading(true);
    mutateDeleteVault({
      vaultIds: selectedLoginsIds,
    }, {
      onSuccess: ({ failCount, successCount }) => {
        if (successCount) {
          showSuccess({
            text: t(
              'organizationLoginsPage.deleteLoginModal.successMessage',
              { count: successCount },
            ),
          });
        }
        if (failCount) {
          showError({
            text: t('organizationLoginsPage.deleteLoginModal.errorMessage', { count: failCount }),
          });
        }
        handleDeleteLoginModalClose();
        resetSelectedRows();
        resetActivePage();
      },
      onError: (e) => {
        showError({ text: t(getTranslationKeyByError(e)) });
        logException(e, {
          message: 'OrganizationLoginsTab/handleDeleteLogin exception',
        });
      },
      onSettled: () => setIsDeleteLoginLoading(false),
    });
  }, [
    selectedLoginsIds,
    mutateDeleteVault,
    handleDeleteLoginModalClose,
    resetSelectedRows,
    resetActivePage,
    showSuccess,
    showError,
    t,
  ]);

  const handleMoveToPrivateLogins = useCallback(async () => {
    try {
      setIsMoveToPrivateLoginsLoading(true);
      await moveVaultsToPrivateKeychain({ vaultIds: selectedLoginsIds });
      if (isTrustedPortalEnabled) {
        showSuccess({ text: t('trustedPortalSuccessNotifications.login.moved') });
      } else {
        showWarning({ text: t('moveLoginsModal.approvalOnMobileToast') });
      }
      resetSelectedRows();
      handleMoveToPrivateLoginsModalClose();
    } catch (e) {
      showError({ text: t(getTranslationKeyByError(e)) });
      logException(e, {
        message: 'OrganizationLoginsTab/handleMoveToPrivateLogins exception',
      });
    } finally {
      setIsMoveToPrivateLoginsLoading(false);
    }
  }, [
    moveVaultsToPrivateKeychain,
    selectedLoginsIds,
    isTrustedPortalEnabled,
    showWarning,
    showError,
    showSuccess,
    handleMoveToPrivateLoginsModalClose,
    resetSelectedRows,
    t,
  ]);

  const moveToPrivateLoginsModalDescriptionElement = useMemo(
    () => (
      <>
        <Typography>{t('moveLoginsToPrivateKeychainModal.description')}</Typography>
        <Typography>{t('moveLoginsToPrivateKeychainModal.additionalDescription')}</Typography>
      </>
    ),
    [t],
  );

  return (
    <PanelContent p={0}>
      <Grid container justifyContent="space-between" alignItems="stretch" p={1}>
        <Grid item xs={4} container flexWrap="nowrap" spacing={1}>
          <Grid item>
            <Tooltip title={t('common.filter')}>
              <FilterButton
                isFilterActive={isFilterActive}
                numberOfActiveFilters={numberOfActiveFilters}
                selected={isFilterOpen}
                onChange={toggleIsFilterOpen}
              />
            </Tooltip>
          </Grid>
          <Grid item my={0.5}>
            <Divider orientation="vertical" />
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={moveToPrivateTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleMoveToPrivateLoginsModalOpen}
                disabled={isMoveToPrivateDisabled}
                variant={SeverityEnum.Error}
              >
                <MoveIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={moveToEmployeeTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleMoveToEmployeeLoginsModalOpen}
                disabled={isMoveToEmployeeDisabled}
              >
                <MoveToEmployeeIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={moveToGroupTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleMoveToGroupLoginsModalOpen}
                disabled={isMoveToGroupDisabled}
              >
                <MoveToGroupIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={shareTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleShareLoginsModalOpen}
                disabled={isShareDisabled}
              >
                <ShareIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={removeTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleDetachLoginModalOpen}
                disabled={isRemoveDisabled}
                variant={SeverityEnum.Error}
              >
                <ErrorIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={deleteTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleDeleteLoginModalOpen}
                disabled={isDeleteDisabled}
                variant={SeverityEnum.Error}
              >
                <DeleteIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item my={0.5}>
            <Divider orientation="vertical" />
          </Grid>
        </Grid>
        <Grid item xs={8} container justifyContent="flex-end" flexWrap="nowrap">
          <Grid item>
            <SearchableTextField
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t('common.search')}
            />
          </Grid>
          <Box mr={3} />
          <Grid item>
            <Button
              icon={<PlusIcon />}
              onClick={handleCreateLoginModalOpen}
            >
              {t('organizationLoginsPage.createLoginButton')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Divider />
      <OrganizationLoginsTable
        selectedLogins={selectedLogins}
        {...restTableProps}
      />
      {isCreateLoginModalOpen && (
        <CreateLoginModal
          isOpen={isCreateLoginModalOpen}
          onClose={handleCreateLoginModalClose}
          onSubmit={handleCreateLogin}
          isLoading={isCreateLoginLoading}
        />
      )}
      {isDetachLoginModalOpen && (
        <PromptModal
          open={isDetachLoginModalOpen}
          onClose={handleDetachLoginModalClose}
          onSubmit={handleDetachLogin}
          title={t(
            `organizationLoginsPage.detachLoginModal.detachFrom${ownership}.title`,
            { count: selectedLogins.size },
          )}
          description={t(
            `organizationLoginsPage.detachLoginModal.detachFrom${ownership}.description`,
          )}
          additionalDescription={t(
            `organizationLoginsPage.detachLoginModal.detachFrom${ownership}.additionalDescription`,
          )}
          list={selectedLoginsAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isDetachLoginLoading}
        />
      )}
      {isDeleteLoginModalOpen && (
        <PromptModal
          open={isDeleteLoginModalOpen}
          onClose={handleDeleteLoginModalClose}
          onSubmit={handleDeleteLogin}
          title={t(
            'organizationLoginsPage.deleteLoginModal.title',
            { count: selectedLogins.size },
          )}
          description={t(
            'organizationLoginsPage.deleteLoginModal.description',
          )}
          approvalButtonText="common.delete"
          list={selectedLoginsAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isDeleteLoginLoading}
        />
      )}
      {isMoveToPrivateLoginsModalOpen && (
        <PromptModal
          open={isMoveToPrivateLoginsModalOpen}
          onClose={handleMoveToPrivateLoginsModalClose}
          onSubmit={handleMoveToPrivateLogins}
          title={t('moveLoginsToPrivateKeychainModal.title', { count: selectedLogins.size })}
          description={moveToPrivateLoginsModalDescriptionElement}
          list={selectedLoginsAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isMoveToPrivateLoginsLoading}
          approvalButtonText="common.move"
          approvalButtonColor="primary"
        />
      )}
      {isMoveToEmployeeLoginsModalOpen && (
        <MoveVaultsToEmployeeModal
          isOpen={isMoveToEmployeeLoginsModalOpen}
          onClose={handleMoveToEmployeeLoginsModalClose}
          vaults={selectedLoginsAsObjects}
          onSubmit={resetSelectedRows}
          type={VaultTypeEnum.Password}
        />
      )}
      {isMoveToGroupLoginsModalOpen && (
        <MoveVaultsToGroupModal
          isOpen={isMoveToGroupLoginsModalOpen}
          onClose={handleMoveToGroupLoginsModalClose}
          vaults={selectedLoginsAsObjects}
          onSubmit={resetSelectedRows}
          type={VaultTypeEnum.Password}
        />
      )}
      {isShareLoginsModalOpen && (
        <ShareVaultsToGroupModal
          isOpen={isShareLoginsModalOpen}
          onClose={handleShareLoginsModalClose}
          vaults={selectedLoginsAsObjects}
          type={VaultTypeEnum.Password}
          onSubmit={resetSelectedRows}
        />
      )}
      <OrganizationLoginsTabFilter
        isOpen={isFilterOpen}
        anchorEl={filterAnchorEl}
        onSubmit={handleFilterSubmit}
        onClose={handleFilterClose}
        initialValues={filterValues}
      />
    </PanelContent>
  );
};

export default OrganizationLoginsTab;
